import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Box, Typography } from '@mui/material';

ChartJS.register(...registerables);

/**
 * MostIssuedItemsChart - Displays issued items as bar chart
 * 
 * @param {Array} data - Array of item data with quantity value (based on selected date range)
 * @param {string} selectedPeriod - Period label for display (optional, for backward compatibility)
 * @param {boolean} showAllPeriods - If true and data has weekly/monthly/yearly, shows grouped bars
 */
const MostIssuedItemsChart = ({ data = [], selectedPeriod = 'weekly', showAllPeriods = false }) => {
    const issuedItemsChartRef = useRef(null);
    const issuedItemsChartInstance = useRef(null);

    // Default data if none provided
    const defaultData = [
        { item_name: 'Arduino Uno', quantity: 0 },
        { item_name: 'Breadboard', quantity: 0 },
        { item_name: 'Jumper Wires', quantity: 0 },
        { item_name: 'Resistors', quantity: 0 },
        { item_name: 'LEDs', quantity: 0 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.item_name || item.name);

    // Check if data has period-based values (for backward compatibility)
    const hasPeriodData = chartData.length > 0 && (
        chartData[0].weekly !== undefined ||
        chartData[0].monthly !== undefined ||
        chartData[0].yearly !== undefined
    );

    // Check if data has simple quantity value (new format)
    const hasQuantityData = chartData.length > 0 && chartData[0].quantity !== undefined;

    // Extract values based on data format
    let chartValues;
    if (hasQuantityData) {
        // New format: single quantity based on date range
        chartValues = chartData.map(item => item.quantity || 0);
    } else if (hasPeriodData) {
        // Old format: period-based values
        chartValues = chartData.map(item => item[selectedPeriod] || 0);
    } else {
        // Fallback
        chartValues = chartData.map(item => item.count || 0);
    }

    // Calculate max value
    const maxVal = Math.max(...chartValues, 0);
    const suggestedMax = maxVal === 0 ? 10 : Math.ceil(maxVal * 1.15);

    // Color definitions for all periods
    const periodColors = {
        weekly: {
            bg: 'rgba(136, 198, 138, 0.35)',
            border: '#88c68a'
        },
        monthly: {
            bg: 'rgba(255, 179, 102, 0.35)',
            border: '#ffb366'
        },
        yearly: {
            bg: 'rgba(255, 153, 153, 0.35)',
            border: '#ff9999'
        }
    };

    useEffect(() => {
        if (issuedItemsChartRef.current) {
            if (issuedItemsChartInstance.current) {
                issuedItemsChartInstance.current.destroy();
            }

            const ctx = issuedItemsChartRef.current.getContext('2d');


            // Build datasets based on data format
            let datasets;
            if (hasQuantityData) {
                // New format: single bar per item showing quantity in selected range
                datasets = [
                    {
                        label: 'Quantity Issued',
                        data: chartValues,
                        backgroundColor: 'rgba(136, 198, 138, 0.35)',
                        borderColor: '#88c68a',
                        borderWidth: 1,
                        barPercentage: 0.6,
                        categoryPercentage: 0.8
                    }
                ];
            } else if (showAllPeriods && hasPeriodData) {
                // Old format with grouped bars (backward compatibility)
                const weeklyValues = chartData.map(item => item.weekly || 0);
                const monthlyValues = chartData.map(item => item.monthly || 0);
                const yearlyValues = chartData.map(item => item.yearly || 0);
                datasets = [
                    {
                        label: 'Weekly',
                        data: weeklyValues,
                        backgroundColor: periodColors.weekly.bg,
                        borderColor: periodColors.weekly.border,
                        borderWidth: 1,
                        barPercentage: 0.7,
                        categoryPercentage: 0.85
                    },
                    {
                        label: 'Monthly',
                        data: monthlyValues,
                        backgroundColor: periodColors.monthly.bg,
                        borderColor: periodColors.monthly.border,
                        borderWidth: 1,
                        barPercentage: 0.7,
                        categoryPercentage: 0.85
                    },
                    {
                        label: 'Yearly',
                        data: yearlyValues,
                        backgroundColor: periodColors.yearly.bg,
                        borderColor: periodColors.yearly.border,
                        borderWidth: 1,
                        barPercentage: 0.7,
                        categoryPercentage: 0.85
                    }
                ];
            } else {
                const colors = periodColors[selectedPeriod] || periodColors.weekly;
                datasets = [
                    {
                        label: 'Quantity',
                        data: chartValues,
                        backgroundColor: colors.bg,
                        borderColor: colors.border,
                        borderWidth: 1,
                        barPercentage: 0.7,
                        categoryPercentage: 0.8
                    }
                ];
            }

            issuedItemsChartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false }, // We use custom legend
                        tooltip: {
                            mode: 'index',
                            intersect: false,
                            callbacks: {
                                label: (context) => `${context.dataset.label}: ${context.raw} items`
                            }
                        },
                        datalabels: { display: false }
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: {
                                color: '#666666',
                                font: { size: 10, family: 'Inter, sans-serif' },
                                maxRotation: 45,
                                minRotation: 45
                            },
                            border: { display: true, color: '#333333' }
                        },
                        y: {
                            min: 0,
                            suggestedMax: suggestedMax,
                            ticks: {
                                precision: 0,
                                color: '#666666',
                                font: { size: 10, family: 'Inter, sans-serif' }
                            },
                            grid: { color: '#f0f0f0', drawBorder: false },
                            border: { display: false }
                        }
                    }
                }
            });
        }
        return () => {
            if (issuedItemsChartInstance.current) {
                issuedItemsChartInstance.current.destroy();
            }
        };
    }, [data, selectedPeriod, showAllPeriods, labels, suggestedMax, chartValues, hasPeriodData, hasQuantityData, chartData]);

    const hasData = chartData.some(item => {
        if (hasQuantityData) {
            return (item.quantity || 0) > 0;
        }
        if (hasPeriodData) {
            return (item.weekly || 0) > 0 || (item.monthly || 0) > 0 || (item.yearly || 0) > 0;
        }
        return (item.count || 0) > 0;
    });

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', gap: { xs: '4px', sm: '10px' } }}>
            <Box sx={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                color: '#333',
                fontSize: { xs: '12px', sm: '14px' },
                fontWeight: 500,
                fontFamily: 'Inter, sans-serif',
                whiteSpace: 'nowrap',
                display: { xs: 'none', sm: 'block' }
            }}>
                No. of Users
            </Box>
            <Box className="group" sx={{ flex: 1, height: '100%', minHeight: { xs: '280px', sm: '320px' }, position: 'relative' }}>
                {!hasData && data.length === 0 ? (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <Typography sx={{ color: '#999', fontSize: '14px' }}>
                            No issued items data available
                        </Typography>
                    </Box>
                ) : (
                    <canvas ref={issuedItemsChartRef} style={{ width: '100%', height: '100%' }}></canvas>
                )}
            </Box>
        </Box>
    );
};

export default MostIssuedItemsChart;
